import { useEffect, useMemo, useRef, useState } from 'react'
import type { ExtractionStage } from '../../../api/listeners/document-extraction'
import { Button } from '../../ui/button'
import { ListenerPassport, RecognizedPassport, recognizedPassportPackets } from './recognized-passport'
import css from './ui.recognition.module.scss'

type Props = {
    stage: ExtractionStage | null
    passport: ListenerPassport
    onPassport: (value: RecognizedPassport, expected: ListenerPassport) => boolean
}

export const RecognizedPassportTransfer = ({ stage, passport, onPassport }: Props) => {
    const baseline = useRef({ ...passport })
    const apply = useRef(onPassport)
    apply.current = onPassport
    const { packets, needsReview } = useMemo(() => recognizedPassportPackets(stage), [stage])
    const [selected, setSelected] = useState<number | null>(null)
    const [state, setState] = useState<{ applied: boolean; message: string; value?: RecognizedPassport }>({ applied: false, message: '' })
    const attempted = useRef('')
    const index = selected ?? (packets.length === 1 && !needsReview ? 0 : null)
    const packet = index === null ? undefined : packets[index]

    useEffect(() => {
        if (!packet) return
        const key = JSON.stringify([stage?.stageID, packet])
        if (attempted.current === key) return
        attempted.current = key
        if (!apply.current(packet, baseline.current)) {
            setState({ applied: false, message: 'Паспортные данные не перенесены: анкета, паспорт или пол уже изменены. Проверьте поля в «Основных данных».' })
            return
        }
        const subject = packet.passport && packet.sex ? 'Паспорт и пол перенесены' : packet.passport ? 'Паспорт перенесён' : 'Пол перенесён'
        setState({ applied: true, message: `${subject} в «Основные данные». Сохраните анкету, чтобы записать изменения.`, value: packet })
    }, [packet, stage?.stageID])

    if (!packets.length && !needsReview) return null
    return <section className={css.group} aria-label="Перенос паспортных данных">
        <h5>Паспорт слушателя и пол</h5>
        {index === null ? <>
            <p className={css.warning}>{packets.length
                ? 'В паспортах разные реквизиты или есть неоднозначный результат. Проверьте оригинал и выберите данные одного паспорта.'
                : 'Паспортные данные требуют проверки. Для переноса паспорта нужны серия, номер, кем выдан и дата выдачи.'}</p>
            {packets.map((value, itemIndex) => <p key={JSON.stringify(value)}><Button onClick={() => setSelected(itemIndex)}>
                Перенести: {[value.passport, value.sex].filter(Boolean).join(' · ')}
            </Button></p>)}
        </> : <>
            <p role="status" aria-live="polite" className={state.applied ? undefined : css.warning}>{state.message}</p>
            {state.value?.passport && <p>{state.value.passport}</p>}
            {state.value?.sex && <p>Пол: {state.value.sex}</p>}
        </>}
    </section>
}
